import { Link } from "@tanstack/react-router";
import { LogOut, UserRound } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIdentityQuery } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { profileInitials } from "@/lib/profile";
import { ThemeToggle } from "./ThemeToggle";

export function PortalAccountMenu() {
  const identity = useIdentityQuery();
  const { signOut } = usePortalRuntime();
  const [signingOut, setSigningOut] = useState(false);
  const displayName = identity.data?.displayName;
  const email = identity.data?.email;

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      toast.error("You could not be signed out. Try again.");
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="flex shrink-0 items-center gap-2">
      <ThemeToggle />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Open profile menu"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xs font-semibold transition-colors hover:border-primary/50 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {profileInitials(displayName, email)}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="space-y-0.5">
            <span className="block truncate text-sm">{displayName || "Subscriber profile"}</span>
            <span className="block truncate text-xs font-normal text-muted-foreground">
              {email || "Authenticated subscriber"}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/profile" className="flex w-full items-center gap-2">
              <UserRound className="h-4 w-4" /> Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={signingOut}
            onSelect={() => void handleSignOut()}
            className="gap-2 text-destructive focus:text-destructive"
          >
            <LogOut className="h-4 w-4" /> {signingOut ? "Signing out…" : "Sign out"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
